/**
 * OBSERVER-FEATURES.WIDGET
 * Features visible to the observers (checked = visible, the options store what is hidden):
 * account settings, and in addition a role
 */
import { useTranslation } from "react-i18next"
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useAppSelector } from "@/core/root-reducer"
import Checkbox from "@/components/checkbox"
import ListItem from "@/components/list-item"
import Tooltip from "@/components/tooltip"
import FilterSelect from "@/components/filter-select"
import Dropdown from "@/components/dropdown"
import { SESSION_DASHBOARD_TABS } from "@/redux/_session.types"
import { ACTION_PLAN_ACCESS, AccountModulesSurvey, getHiddenFilterNames, ObserverOptions } from "@/redux/account.types"

type ObserverFlag = Exclude<
  keyof ObserverOptions,
  | "dashboardTabsHiddenForObserver"
  | "hiddenFiltersForObserver"
  | "actionPlanAccessForObserver"
  | "showAccountCompareForObserver"
>

const FLAGS: { key: ObserverFlag; label: string; module?: keyof AccountModulesSurvey }[] = [
  { key: "hideSummarizeForObserver", label: "module_summarize", module: "summarize" },
  { key: "hideAssistantForObserver", label: "module_assistant", module: "assistant" },
  { key: "hideReportForObserver", label: "reports" }
]

interface Props {
  options: Partial<ObserverOptions> //Hidden features (checked)
  locked?: Partial<ObserverOptions> //Already hidden by the account: cannot be shown again
  isRole?: boolean //The action plans access of a role replaces the account's ("as the account" by default); two columns, no separator
  onChange: (changes: Partial<ObserverOptions>) => void
}

//Locked: hidden by the account, the "i" says why it cannot be checked
function FeatureCheckbox({ text, isHidden, isLocked, onClick }: { text: string; isHidden: boolean; isLocked: boolean; onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="my-[16px] flex items-center gap-2">
      <Checkbox className="!ml-0" active={!isLocked && !isHidden} disabled={isLocked} text={text} onClick={onClick} />
      {isLocked && (
        <Tooltip text={t("roles_observer_options_locked")} width={280} className="!flex items-center leading-none" usePortal>
          <FontAwesomeIcon icon={faInfoCircle} className="grey-t cursor-help" aria-label={t("roles_observer_options_locked")} />
        </Tooltip>
      )}
    </div>
  )
}

export default function ObserverFeaturesWidget({ options, locked = {}, isRole, onChange }: Props) {
  const { t } = useTranslation()
  const modules = useAppSelector((state) => state._session.modules.survey)
  const hiddenTabs = options.dashboardTabsHiddenForObserver ?? []
  const hiddenFilters = getHiddenFilterNames(options.hiddenFiltersForObserver)
  const lockedTabs = locked.dashboardTabsHiddenForObserver ?? []

  //Only the features of the account's modules
  const isModuleActive = (module?: keyof AccountModulesSurvey) => !module || modules[module]
  //The internal tabs (only for the team of IDTree) are never offered to the client
  const tabs = SESSION_DASHBOARD_TABS.filter(
    (tab) => tab.role !== "ADMIN" && !tab.isInternal && isModuleActive(tab.module ?? tab.upsell)
  )

  //Action plans: the role adds "as the account", which removes its own choice
  const actionPlanAccessList = [
    ...(isRole ? [{ id: "", name: t("action_plan_access_account") }] : []),
    ...ACTION_PLAN_ACCESS.map((access) => ({ id: access, name: t("action_plan_access_" + access) }))
  ]
  const actionPlanAccess = options.actionPlanAccessForObserver ?? (isRole ? "" : "self")

  return (
    <>
      <ListItem isWithoutBorder={isRole}>
        <div>
          <b>{t("account_dashboard_observer_visible")}</b>
          {tabs.map((tab) => (
            <FeatureCheckbox
              key={tab.id}
              text={t(tab.label)}
              isHidden={hiddenTabs.includes(tab.id)}
              isLocked={lockedTabs.includes(tab.id)}
              onClick={() =>
                onChange({
                  dashboardTabsHiddenForObserver: hiddenTabs.includes(tab.id)
                    ? hiddenTabs.filter((x) => x !== tab.id)
                    : [...hiddenTabs, tab.id]
                })
              }
            />
          ))}

          {FLAGS.filter((flag) => isModuleActive(flag.module)).map((flag) => (
            <FeatureCheckbox
              key={flag.key}
              text={t(flag.label)}
              isHidden={Boolean(options[flag.key])}
              isLocked={Boolean(locked[flag.key])}
              onClick={() => onChange({ [flag.key]: !options[flag.key] })}
            />
          ))}
        </div>
      </ListItem>

      <ListItem isWithoutBorder={isRole}>
        <div>
          <b>{t("account_dashboard_filters_observer_hide")}</b>
          <div className="mt-[16px]">
            <FilterSelect
              names={hiddenFilters}
              title={t("account_dashboard_filters_observer_hide")}
              help={t("account_dashboard_filters_observer_hide_help")}
              example=""
              onSave={async (names) => {
                onChange({ hiddenFiltersForObserver: names.join(", ") })
                return true
              }}
            />
          </div>
        </div>
      </ListItem>

      {/** Shown by the account or by the role: checked and locked in the role when the account shows it */}
      <ListItem isWithoutBorder={isRole}>
        {/** Natural height: the checkbox container is 46px high, the box at its top */}
        <Checkbox
          className="!ml-0 !h-auto"
          active={Boolean(locked.showAccountCompareForObserver || options.showAccountCompareForObserver)}
          disabled={Boolean(locked.showAccountCompareForObserver)}
          text={t("account_compare_observer")}
          onClick={() => onChange({ showAccountCompareForObserver: !options.showAccountCompareForObserver })}
        />
      </ListItem>

      {modules.task && (
        <ListItem isWithoutBorder={isRole}>
          <div>
            <b>{t("action_plan_access")}</b>
            <div className="mt-[16px]">
              <Dropdown
                isFullWidth
                displayField="name"
                //In the role modal, the list opens upwards: below is the end of the modal
                isContextMenuTop={isRole}
                active={actionPlanAccess}
                value={actionPlanAccessList.find((access) => access.id === actionPlanAccess)?.name ?? ""}
                list={actionPlanAccessList}
                onSelect={(access: { id: string }) =>
                  onChange({
                    actionPlanAccessForObserver: (access.id || undefined) as ObserverOptions["actionPlanAccessForObserver"]
                  })
                }
              />
            </div>
          </div>
        </ListItem>
      )}
    </>
  )
}
